export interface IPersonalSettings {
	warningIndicators: boolean;
	errorIndicators: boolean;
	productDepthShelf: ProductDepthType;
	productDepthHook: ProductDepthType;
	productDepthBox: ProductDepthType;
	productHeightBox: ProductDepthType;
	productDepthPallet: ProductDepthType;
	planogramImagesMode: boolean;
	transparentProductImagesBackground: boolean;
	displayProductImageFrame: boolean;
	displayInactiveProductImageFrame: boolean;
	sequenceNumberMode: boolean;
	blockPercentsShow: boolean;
	planRulers: boolean;
	planGrid: boolean;
	planBackImage: boolean;
	packingOffset: boolean;
	transparentBoxesMode: boolean;
	dividePlanogramConfigurationImg: boolean;
	planogramProductSvg: PlanogramProductSvgType;
	blockNameWrap: boolean;
	blockFontSize: BlockFontSizeType;
	timeTrackerEnable: boolean;
	searchAlgorithm: SearchAlgorithmType;
    notifications: Array<NotificationsTypes>;
    notificationsWebDisabled: Array<string>;
	notificationsWebPushDisabled: Array<string>;
	productRotationPoint: ProductRotationPointType;
	planogramEditTooltipFields: Array<PlanogramEditTooltipField>;
}

export type ProductDepthType = -1 | 1;

export type PlanogramProductSvgType = 'edit' | 'image' | 'article';

export type BlockFontSizeType = 'small' | 'medium' | 'large';

export type SearchAlgorithmType = 'exact' | 'keyword';

export enum NotificationsTypes {
    NewExecutionEmail = 'NEW_EXECUTION_EMAIL',
    NewExecutionApp = 'NEW_EXECUTION_APP',
    ReturnedOnExecutionApp = 'RETURNED_ON_EXECUTION_APP',
    CanceledExecutionApp = 'CANCELED_EXECUTION_APP',
    ExecutionDoneEmail = 'EXECUTION_DONE_EMAIL',
    ExecutionDoneApp = 'EXECUTION_DONE_APP',
    NewTaskApp = 'NEW_TASK_APP',
    DeadlineApp = 'DEADLINE_APP'
}

export type ProductRotationPointType = 'center' | 'bottomLeft' | 'topLeft';

export type PlanogramEditTooltipField =
	| 'sequenceNumber'
	| 'facesQty'
	| 'totalPlanFacesQty'
	| 'recommendedFacesQty'
	| 'placementDepth'
	| 'placementHeight'
	| 'price'
	| 'stockQty';

export const DEFAULT_PERSONAL_SETTINGS: IPersonalSettings = {
	warningIndicators: true,
	errorIndicators: true,
	productDepthShelf: -1,
	productDepthHook: 1,
	productDepthBox: 1,
	productHeightBox: 1,
	productDepthPallet: 1,
	planogramImagesMode: true,
	transparentProductImagesBackground: true,
	displayProductImageFrame: true,
	displayInactiveProductImageFrame: true,
	sequenceNumberMode: false,
	blockPercentsShow: true,
	planRulers: true,
	planGrid: true,
	planBackImage: true,
	packingOffset: true,
	transparentBoxesMode: false,
	dividePlanogramConfigurationImg: false,
	planogramProductSvg: 'edit',
	blockNameWrap: false,
	blockFontSize: 'small',
	timeTrackerEnable: false,
	searchAlgorithm: 'keyword',
    notifications: Object.values(NotificationsTypes),
	notificationsWebDisabled: [],
	notificationsWebPushDisabled: [],
	productRotationPoint: 'center',
	planogramEditTooltipFields: [
		'sequenceNumber',
		'facesQty',
		'totalPlanFacesQty',
		'recommendedFacesQty',
		'placementDepth',
		'placementHeight',
		'price'
	]
};

export class PersonalSettings {
	private _warningIndicators: boolean;
	private _errorIndicators: boolean;
	private _blockFontSize: BlockFontSizeType;
	private _blockNameWrap: boolean;
	private _blockPercentsShow: boolean;
	private _dividePlanogramConfigurationImg: boolean;
    private _notifications: Array<NotificationsTypes>;
    private _notificationsWebDisabled: Array<string>;
    private _notificationsWebPushDisabled: Array<string>;
	private _packingOffset: boolean;
	private _planBackImage: boolean;
	private _planGrid: boolean;
	private _planogramImagesMode: boolean;
	private _transparentProductImagesBackground: boolean;
	private _displayProductImageFrame: boolean;
	private _displayInactiveProductImageFrame: boolean;
	private _sequenceNumberMode: boolean;
	private _planogramProductSvg: PlanogramProductSvgType;
	private _planRulers: boolean;
	private _productDepthHook: ProductDepthType;
	private _productDepthShelf: ProductDepthType;
	private _productDepthBox: ProductDepthType;
	private _productHeightBox: ProductDepthType;
	private _productDepthPallet: ProductDepthType;
	private _productRotationPoint: ProductRotationPointType;
	private _searchAlgorithm: SearchAlgorithmType;
	private _timeTrackerEnable: boolean;
	private _transparentBoxesMode: boolean;
	private _planogramEditTooltipFields: Array<PlanogramEditTooltipField>;

	constructor(settings: Partial<IPersonalSettings> = {}) {
		this.warningIndicators = settings.warningIndicators;
		this.errorIndicators = settings.errorIndicators;
		this.blockFontSize = settings.blockFontSize;
		this.blockNameWrap = settings.blockNameWrap;
		this.blockPercentsShow = settings.blockPercentsShow;
		this.dividePlanogramConfigurationImg = settings.dividePlanogramConfigurationImg;
        this.notifications = settings.notifications;
		this.notificationsWebDisabled = settings.notificationsWebDisabled;
		this.notificationsWebPushDisabled = settings.notificationsWebPushDisabled;
		this.packingOffset = settings.packingOffset;
		this.planBackImage = settings.planBackImage;
		this.planGrid = settings.planGrid;
		this.planogramImagesMode = settings.planogramImagesMode;
		this.transparentProductImagesBackground = settings.transparentProductImagesBackground;
		this.displayProductImageFrame = settings.displayProductImageFrame;
		this.displayInactiveProductImageFrame = settings.displayInactiveProductImageFrame;
		this.sequenceNumberMode = settings.sequenceNumberMode;
		this.planogramProductSvg = settings.planogramProductSvg;
		this.planRulers = settings.planRulers;
		this.productDepthBox = settings.productDepthBox;
		this.productHeightBox = settings.productHeightBox;
		this.productDepthPallet = settings.productDepthPallet;
		this.productDepthHook = settings.productDepthHook;
		this.productDepthShelf = settings.productDepthShelf;
        this.productRotationPoint = settings.productRotationPoint;
		this.searchAlgorithm = settings.searchAlgorithm;
		this.timeTrackerEnable = settings.timeTrackerEnable;
		this.transparentBoxesMode = settings.transparentBoxesMode;
		this.planogramEditTooltipFields = settings.planogramEditTooltipFields;
	}

	set warningIndicators(value: boolean) {
		this._warningIndicators = value ?? DEFAULT_PERSONAL_SETTINGS.warningIndicators;
	}

	get warningIndicators(): boolean {
		return this._warningIndicators ?? DEFAULT_PERSONAL_SETTINGS.warningIndicators;
	}

	set errorIndicators(value: boolean) {
		this._errorIndicators = value ?? DEFAULT_PERSONAL_SETTINGS.errorIndicators;
	}

	get errorIndicators(): boolean {
		return this._errorIndicators ?? DEFAULT_PERSONAL_SETTINGS.errorIndicators;
	}

	set blockFontSize(value: BlockFontSizeType | undefined) {
		this._blockFontSize = value ?? DEFAULT_PERSONAL_SETTINGS.blockFontSize;
	}

	get blockFontSize(): BlockFontSizeType {
		return this._blockFontSize ?? DEFAULT_PERSONAL_SETTINGS.blockFontSize;
	}

	set blockNameWrap(value: boolean | undefined) {
		this._blockNameWrap = value ?? DEFAULT_PERSONAL_SETTINGS.blockNameWrap;
	}

	get blockNameWrap(): boolean {
		return this._blockNameWrap ?? DEFAULT_PERSONAL_SETTINGS.blockNameWrap;
	}

	set blockPercentsShow(value: boolean | undefined) {
		this._blockPercentsShow = value ?? DEFAULT_PERSONAL_SETTINGS.blockPercentsShow;
	}

	get blockPercentsShow(): boolean {
		return this._blockPercentsShow ?? DEFAULT_PERSONAL_SETTINGS.blockPercentsShow;
	}

	set dividePlanogramConfigurationImg(value: boolean | undefined) {
		this._dividePlanogramConfigurationImg = value ?? DEFAULT_PERSONAL_SETTINGS.dividePlanogramConfigurationImg;
	}

	get dividePlanogramConfigurationImg(): boolean {
		return this._dividePlanogramConfigurationImg ?? DEFAULT_PERSONAL_SETTINGS.dividePlanogramConfigurationImg;
	}

    set notifications(value: Array<NotificationsTypes> | undefined) {
        this._notifications = value ?? DEFAULT_PERSONAL_SETTINGS.notifications;
    }

    get notifications(): Array<NotificationsTypes> {
        return this._notifications ?? DEFAULT_PERSONAL_SETTINGS.notifications;
    }

	set notificationsWebDisabled(value: Array<string> | undefined) {
		this._notificationsWebDisabled = value ?? DEFAULT_PERSONAL_SETTINGS.notificationsWebDisabled;
	}

	get notificationsWebDisabled(): Array<string> {
		return this._notificationsWebDisabled ?? DEFAULT_PERSONAL_SETTINGS.notificationsWebDisabled;
	}

	set notificationsWebPushDisabled(value: Array<string> | undefined) {
		this._notificationsWebPushDisabled = value ?? DEFAULT_PERSONAL_SETTINGS.notificationsWebPushDisabled;
	}

	get notificationsWebPushDisabled(): Array<string> {
		return this._notificationsWebPushDisabled ?? DEFAULT_PERSONAL_SETTINGS.notificationsWebPushDisabled;
	}

	set packingOffset(value: boolean | undefined) {
		this._packingOffset = value ?? DEFAULT_PERSONAL_SETTINGS.packingOffset;
	}

	get packingOffset(): boolean {
		return this._packingOffset ?? DEFAULT_PERSONAL_SETTINGS.packingOffset;
	}

	set planBackImage(value: boolean | undefined) {
		this._planBackImage = value ?? DEFAULT_PERSONAL_SETTINGS.planBackImage;
	}

	get planBackImage(): boolean {
		return this._planBackImage ?? DEFAULT_PERSONAL_SETTINGS.planBackImage;
	}

	set planGrid(value: boolean | undefined) {
		this._planGrid = value ?? DEFAULT_PERSONAL_SETTINGS.planGrid;
	}

	get planGrid(): boolean {
		return this._planGrid ?? DEFAULT_PERSONAL_SETTINGS.planGrid;
	}

	set planogramImagesMode(value: boolean | undefined) {
		this._planogramImagesMode = value ?? DEFAULT_PERSONAL_SETTINGS.planogramImagesMode;
	}

	get planogramImagesMode(): boolean {
		return this._planogramImagesMode ?? DEFAULT_PERSONAL_SETTINGS.planogramImagesMode;
	}

	set transparentProductImagesBackground(value: boolean | undefined) {
		this._transparentProductImagesBackground = value ?? DEFAULT_PERSONAL_SETTINGS.transparentProductImagesBackground;
	}

	get transparentProductImagesBackground(): boolean {
		return this._transparentProductImagesBackground ?? DEFAULT_PERSONAL_SETTINGS.transparentProductImagesBackground;
	}

	set displayProductImageFrame(value: boolean | undefined) {
		this._displayProductImageFrame = value ?? DEFAULT_PERSONAL_SETTINGS.displayProductImageFrame;
	}

	get displayProductImageFrame(): boolean {
		return this._displayProductImageFrame ?? DEFAULT_PERSONAL_SETTINGS.displayProductImageFrame;
	}

	set displayInactiveProductImageFrame(value: boolean | undefined) {
		this._displayInactiveProductImageFrame = value ?? DEFAULT_PERSONAL_SETTINGS.displayInactiveProductImageFrame;
	}

	get displayInactiveProductImageFrame(): boolean {
		return this._displayInactiveProductImageFrame ?? DEFAULT_PERSONAL_SETTINGS.displayInactiveProductImageFrame;
	}

	set sequenceNumberMode(value: boolean | undefined) {
		this._sequenceNumberMode = value ?? DEFAULT_PERSONAL_SETTINGS.sequenceNumberMode;
	}

	get sequenceNumberMode(): boolean {
		return this._sequenceNumberMode ?? DEFAULT_PERSONAL_SETTINGS.sequenceNumberMode;
	}

	set planogramProductSvg(value: PlanogramProductSvgType | undefined) {
		this._planogramProductSvg = value ?? DEFAULT_PERSONAL_SETTINGS.planogramProductSvg;
	}

	get planogramProductSvg(): PlanogramProductSvgType {
		return this._planogramProductSvg ?? DEFAULT_PERSONAL_SETTINGS.planogramProductSvg;
	}

	set planRulers(value: boolean | undefined) {
		this._planRulers = value ?? DEFAULT_PERSONAL_SETTINGS.planRulers;
	}

	get planRulers(): boolean {
		return this._planRulers ?? DEFAULT_PERSONAL_SETTINGS.planRulers;
	}

	set productDepthHook(value: ProductDepthType | undefined) {
		this._productDepthHook = value ?? DEFAULT_PERSONAL_SETTINGS.productDepthHook;
	}

	get productDepthHook(): ProductDepthType {
		return this._productDepthHook ?? DEFAULT_PERSONAL_SETTINGS.productDepthHook;
	}

	set productDepthShelf(value: ProductDepthType | undefined) {
		this._productDepthShelf = value ?? DEFAULT_PERSONAL_SETTINGS.productDepthShelf;
	}

	get productDepthShelf(): ProductDepthType {
		return this._productDepthShelf ?? DEFAULT_PERSONAL_SETTINGS.productDepthShelf;
	}

	set productDepthBox(value: ProductDepthType | undefined) {
		this._productDepthBox = value ?? DEFAULT_PERSONAL_SETTINGS.productDepthBox;
	}

	get productDepthBox(): ProductDepthType {
		return this._productDepthBox ?? DEFAULT_PERSONAL_SETTINGS.productDepthBox;
	}

	set productHeightBox(value: ProductDepthType | undefined) {
		this._productHeightBox = value ?? DEFAULT_PERSONAL_SETTINGS.productHeightBox;
	}

	get productHeightBox(): ProductDepthType {
		return this._productHeightBox ?? DEFAULT_PERSONAL_SETTINGS.productHeightBox;
	}

	set productDepthPallet(value: ProductDepthType | undefined) {
		this._productDepthPallet = value ?? DEFAULT_PERSONAL_SETTINGS.productDepthPallet;
	}

	get productDepthPallet(): ProductDepthType {
		return this._productDepthPallet ?? DEFAULT_PERSONAL_SETTINGS.productDepthPallet;
	}

	set productRotationPoint(value: ProductRotationPointType | undefined) {
		this._productRotationPoint = value ?? DEFAULT_PERSONAL_SETTINGS.productRotationPoint;
	}

	get productRotationPoint(): ProductRotationPointType {
		return this._productRotationPoint ?? DEFAULT_PERSONAL_SETTINGS.productRotationPoint;
	}

	set searchAlgorithm(value: SearchAlgorithmType | undefined) {
		this._searchAlgorithm = value ?? DEFAULT_PERSONAL_SETTINGS.searchAlgorithm;
	}

	get searchAlgorithm(): SearchAlgorithmType {
		return this._searchAlgorithm ?? DEFAULT_PERSONAL_SETTINGS.searchAlgorithm;
	}

	set timeTrackerEnable(value: boolean | undefined) {
		this._timeTrackerEnable = value ?? DEFAULT_PERSONAL_SETTINGS.timeTrackerEnable;
	}

	get timeTrackerEnable(): boolean {
		return this._timeTrackerEnable ?? DEFAULT_PERSONAL_SETTINGS.timeTrackerEnable;
	}

	set transparentBoxesMode(value: boolean | undefined) {
		this._transparentBoxesMode = value ?? DEFAULT_PERSONAL_SETTINGS.transparentBoxesMode;
	}

	get transparentBoxesMode(): boolean {
		return this._transparentBoxesMode ?? DEFAULT_PERSONAL_SETTINGS.transparentBoxesMode;
	}

	set planogramEditTooltipFields(value: Array<PlanogramEditTooltipField> | undefined) {
		this._planogramEditTooltipFields = value ?? DEFAULT_PERSONAL_SETTINGS.planogramEditTooltipFields;
	}

	get planogramEditTooltipFields(): Array<PlanogramEditTooltipField> {
		return this._planogramEditTooltipFields ?? DEFAULT_PERSONAL_SETTINGS.planogramEditTooltipFields;
	}

	get settings(): IPersonalSettings {
		return {
			warningIndicators: this.warningIndicators,
			errorIndicators: this.errorIndicators,
			blockFontSize: this.blockFontSize,
			blockNameWrap: this.blockNameWrap,
			blockPercentsShow: this.blockPercentsShow,
			dividePlanogramConfigurationImg: this.dividePlanogramConfigurationImg,
            notifications: this.notifications,
			notificationsWebDisabled: this.notificationsWebDisabled,
			notificationsWebPushDisabled: this.notificationsWebPushDisabled,
			planBackImage: this.planBackImage,
			planGrid: this.planGrid,
			productDepthHook: this.productDepthHook,
			productDepthShelf: this.productDepthShelf,
			productDepthBox: this.productDepthBox,
			productHeightBox: this.productHeightBox,
			productDepthPallet: this.productDepthPallet,
			productRotationPoint: this.productRotationPoint,
			planogramImagesMode: this.planogramImagesMode,
			transparentProductImagesBackground: this.transparentProductImagesBackground,
			displayProductImageFrame: this.displayProductImageFrame,
			displayInactiveProductImageFrame: this.displayInactiveProductImageFrame,
			sequenceNumberMode: this.sequenceNumberMode,
			planogramProductSvg: this.planogramProductSvg,
			planRulers: this.planRulers,
			packingOffset: this.packingOffset,
			searchAlgorithm: this.searchAlgorithm,
			timeTrackerEnable: this.timeTrackerEnable,
			transparentBoxesMode: this.transparentBoxesMode,
			planogramEditTooltipFields: this.planogramEditTooltipFields
		};
	}
}
